import React, { useState, useEffect } from 'react';
import { 
  ClipboardCheck, Search, Users, CheckCircle2, 
  XCircle, Clock, AlertCircle, Save, Calendar, BookOpen,
  ChevronRight, Info, History, ArrowLeft, User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { useToast } from '../context/ToastContext';
import { Topbar } from '../components/Topbar';
import { Card, Button, Badge, Skeleton } from '../components/ui';
import { motion, AnimatePresence } from 'framer-motion';

export function ProfessorAttendancePage() {
  const { user } = useAuth();
  const data = useData();
  const { addToast } = useToast();
  const loading = useLoading(500);

  // States
  const [selectedClass, setSelectedClass] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Carregar chamada existente se houver para hoje
  useEffect(() => {
    if (selectedClass && data?.attendanceRecords) {
      const today = new Date().toLocaleDateString();
      const existingRecord = data.attendanceRecords.find(
        r => r.classId === selectedClass.id && r.date === today
      );
      if (existingRecord) {
        setAttendance(existingRecord.attendance || {});
      } else {
        setAttendance({});
      }
    }
  }, [selectedClass, data?.attendanceRecords]);

  if (!user || user.type !== 'professor' || !data) return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="p-10 text-center">
        <AlertCircle size={48} className="text-red mx-auto mb-4" />
        <h2 className="text-xl font-bold">Acesso Negado</h2>
        <p className="text-muted">Não foi possível carregar as informações de frequência.</p>
        <Button className="mt-6" onClick={() => window.location.reload()}>Tentar Novamente</Button>
      </Card>
    </div>
  );

  const { allEnrollments, attendanceRecords, registerAttendance } = data;

  // Filtrar cursos do professor logado
  const profCourses = user?.courses ? user.courses.split(',').map(c => c.trim()).filter(c => c !== '') : [];
  
  const myClasses = [];
  profCourses.forEach(courseName => {
    const studentsA = (allEnrollments || []).filter(e => (e.course === courseName || e.courseName === courseName) && e.turma === 'A');
    const studentsB = (allEnrollments || []).filter(e => (e.course === courseName || e.courseName === courseName) && e.turma === 'B');
    
    myClasses.push({ id: `${courseName}-A`, course: courseName, turma: 'A', count: studentsA.length });
    myClasses.push({ id: `${courseName}-B`, course: courseName, turma: 'B', count: studentsB.length });
  });

  const studentsInSelectedClass = selectedClass ? (allEnrollments || []).filter(e => 
    (e.course === selectedClass.course || e.courseName === selectedClass.course) && e.turma === selectedClass.turma
  ) : [];

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleSave = () => {
    const totalStudents = studentsInSelectedClass.length;
    const recordedCount = Object.keys(attendance).length;

    if (recordedCount < totalStudents) {
      addToast(`Atenção: Você registrou apenas ${recordedCount} de ${totalStudents} alunos.`, 'warning');
    }

    registerAttendance(selectedClass.id, new Date().toLocaleDateString(), attendance);
    addToast(`Chamada da turma ${selectedClass.course} - ${selectedClass.turma} salva!`, 'success');
    setSelectedClass(null);
    setAttendance({});
  };

  const getStudentStats = (studentId) => {
    const records = (attendanceRecords || []).filter(r => r.attendance && r.attendance[studentId]);
    const total = records.length;
    if (total === 0) return { percent: 100, p: 0, f: 0, j: 0 };
    
    const p = records.filter(r => r.attendance[studentId] === 'P' || r.attendance[studentId] === 'A').length;
    const f = records.filter(r => r.attendance[studentId] === 'F').length;
    const j = records.filter(r => r.attendance[studentId] === 'J').length;
    
    return {
      percent: Math.round((p / total) * 100),
      p, f, j
    };
  };

  const summary = {
    p: Object.values(attendance).filter(v => v === 'P').length,
    f: Object.values(attendance).filter(v => v === 'F').length,
    a: Object.values(attendance).filter(v => v === 'A').length,
    j: Object.values(attendance).filter(v => v === 'J').length,
    total: studentsInSelectedClass.length
  };

  const recordedCount = Object.keys(attendance).length;
  const progress = summary.total > 0 ? Math.min(100, Math.round((recordedCount / summary.total) * 100)) : 0;

  return (
    <div className="min-h-screen pb-20">
      <Topbar />
      
      <div className="max-w-7xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          {!selectedClass ? (
            <motion.div 
              key="class-list"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-10"
            >
              <div className="flex justify-between items-end">
                <div>
                  <h2 className="text-4xl font-black tracking-tight uppercase">Gestão de Frequência</h2>
                  <p className="text-muted font-bold text-sm uppercase tracking-widest mt-2">Selecione uma turma para realizar a chamada ou consultar histórico.</p>
                </div>
                <Button variant="outline" className="flex items-center gap-2" onClick={() => setShowHistory(!showHistory)}>
                  <History size={18} /> {showHistory ? 'Ocultar Histórico' : 'Ver Histórico Geral'}
                </Button>
              </div>

              {myClasses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {myClasses.map((cls) => (
                    <button 
                      key={cls.id}
                      onClick={() => setSelectedClass(cls)}
                      className={`group text-left border border-white/10 p-7 rounded-[2rem] transition-all relative overflow-hidden hover:border-purple/30 hover:shadow-[0_25px_70px_rgba(0,0,0,0.45)] ${
                        cls.turma === 'A'
                          ? 'bg-gradient-to-br from-purple/10 via-[#0a1120] to-[#0a1120]'
                          : 'bg-gradient-to-br from-blue/10 via-[#0a1120] to-[#0a1120]'
                      }`}
                    >
                      <div
                        className={`absolute top-0 right-0 w-44 h-44 blur-[70px] -mr-24 -mt-24 transition-all ${
                          cls.turma === 'A' ? 'bg-purple/20 group-hover:bg-purple/30' : 'bg-blue/20 group-hover:bg-blue/30'
                        }`}
                      />
                      <div className="relative flex items-center justify-between gap-6">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3">
                            <Badge
                              variant="purple"
                              className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                cls.turma === 'A' ? '' : 'bg-blue/20 text-blue border-blue/30'
                              }`}
                            >
                              Turma {cls.turma}
                            </Badge>
                            <span className="text-[10px] font-black uppercase tracking-[3px] text-muted">Frequência</span>
                          </div>
                          <h3 className="mt-4 text-2xl font-black text-white uppercase leading-tight truncate">{cls.course}</h3>
                          <div className="mt-3 flex items-center gap-2 text-muted">
                            <Users size={16} />
                            <span className="text-xs font-bold uppercase tracking-widest">{cls.count} alunos</span>
                          </div>
                        </div>

                        <div className="shrink-0 flex items-center gap-3">
                          <span className="hidden sm:inline text-[10px] font-black uppercase tracking-[3px] text-white/60">
                            Abrir
                          </span>
                          <div
                            className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all border ${
                              cls.turma === 'A'
                                ? 'bg-purple/10 border-purple/20 text-purple group-hover:bg-purple group-hover:text-white group-hover:border-purple'
                                : 'bg-blue/10 border-blue/20 text-blue group-hover:bg-blue group-hover:text-white group-hover:border-blue'
                            }`}
                          >
                            <ChevronRight size={20} />
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white/5 rounded-[3rem] border-2 border-dashed border-white/10">
                  <BookOpen size={48} className="mx-auto mb-4 opacity-20" />
                  <h3 className="text-xl font-black">Você ainda não possui turmas vinculadas.</h3>
                  <p className="text-muted mt-2">Entre em contato com a secretaria para atribuição de cursos.</p>
                </div>
              )}

              {showHistory && (
                <div className="mt-12 space-y-6">
                  <h3 className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                    <History className="text-purple" /> Histórico de Chamadas Recentes
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(attendanceRecords || []).slice().reverse().map(record => (
                      <div key={record.id} className="bg-white/5 p-6 rounded-3xl border border-white/5 flex justify-between items-center">
                        <div>
                          <strong className="text-sm block uppercase">{record.classId.replace('-', ' - ')}</strong>
                          <span className="text-[10px] text-muted font-bold uppercase tracking-widest">{record.date}</span>
                        </div>
                        <div className="flex gap-4">
                          <div className="text-center">
                            <span className="block text-green font-black text-lg">{Object.values(record.attendance).filter(v => v === 'P' || v === 'A').length}</span>
                            <span className="text-[8px] uppercase font-black opacity-40">Pres.</span>
                          </div>
                          <div className="text-center">
                            <span className="block text-red font-black text-lg">{Object.values(record.attendance).filter(v => v === 'F').length}</span>
                            <span className="text-[8px] uppercase font-black opacity-40">Faltas</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              key="attendance-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedClass(null)}
                    style={{ borderRadius: 14, height: 40, padding: '0 12px' }}
                  >
                    <ArrowLeft size={18} /> Voltar
                  </Button>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em' }}>
                        {selectedClass.course}
                      </h2>
                      <span
                        className="badge"
                        style={{
                          background: 'rgba(139,92,246,0.16)',
                          border: '1px solid rgba(139,92,246,0.28)',
                          color: 'var(--purple)',
                          borderRadius: 999,
                          padding: '6px 10px',
                          fontSize: 11,
                          fontWeight: 900
                        }}
                      >
                        Turma {selectedClass.turma}
                      </span>
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 12, fontWeight: 700 }}>
                      <Calendar size={14} />
                      <span>{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</span>
                    </div>
                  </div>
                </div>

                <Button
                  variant="secondary"
                  onClick={handleSave}
                  style={{
                    borderRadius: 14,
                    height: 40,
                    padding: '0 14px',
                    border: 0,
                    background: 'linear-gradient(135deg, var(--purple), var(--purple2))',
                    color: '#fff',
                    fontWeight: 900,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 14px 32px rgba(139,92,246,.18)'
                  }}
                >
                  <Save size={16} /> Salvar
                </Button>
              </div>

              <div className="panel" style={{ padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(67,214,111,.22)', background: 'rgba(67,214,111,.10)', color: 'var(--green)', fontSize: 11, fontWeight: 900 }}>
                    P {summary.p}
                  </span>
                  <span style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(255,77,109,.22)', background: 'rgba(255,77,109,.08)', color: 'var(--red)', fontSize: 11, fontWeight: 900 }}>
                    F {summary.f}
                  </span>
                  <span style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(245,158,11,.22)', background: 'rgba(245,158,11,.10)', color: 'var(--orange)', fontSize: 11, fontWeight: 900 }}>
                    A {summary.a}
                  </span>
                  <span style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(56,189,248,.22)', background: 'rgba(56,189,248,.10)', color: 'var(--blue)', fontSize: 11, fontWeight: 900 }}>
                    J {summary.j}
                  </span>
                </div>

                <div style={{ minWidth: 220 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)' }}>Registrados</span>
                    <strong style={{ fontSize: 12, fontWeight: 900 }}>{recordedCount}/{summary.total}</strong>
                  </div>
                  <div className="track mini">
                    <div style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>

              <Card>
                <div className="tableWrap">
                  {studentsInSelectedClass.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Estudante</th>
                          <th>Matrícula</th>
                          <th style={{ textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentsInSelectedClass.map(student => (
                          <tr key={student.enrollmentId}>
                            <td>
                              <button
                                onClick={() => setSelectedStudent(student)}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', padding: 0 }}
                              >
                                <div
                                  style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 12,
                                    background: 'rgba(139,92,246,.14)',
                                    border: '1px solid rgba(139,92,246,.22)',
                                    color: 'var(--purple)',
                                    display: 'grid',
                                    placeItems: 'center',
                                    fontWeight: 900
                                  }}
                                >
                                  {student.fullName?.[0] || 'A'}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                  <strong style={{ fontSize: 13 }}>{student.fullName}</strong>
                                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>Detalhes</span>
                                </div>
                              </button>
                            </td>
                            <td>
                              <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 12, color: 'var(--purple)', fontWeight: 800 }}>
                                {student.enrollmentId}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'inline-flex', gap: 8 }}>
                                <StatusBtn
                                  active={attendance[student.enrollmentId] === 'P'}
                                  onClick={() => handleStatusChange(student.enrollmentId, 'P')}
                                  variant="green" label="P" title="Presente"
                                />
                                <StatusBtn
                                  active={attendance[student.enrollmentId] === 'F'}
                                  onClick={() => handleStatusChange(student.enrollmentId, 'F')}
                                  variant="red" label="F" title="Falta"
                                />
                                <StatusBtn
                                  active={attendance[student.enrollmentId] === 'A'}
                                  onClick={() => handleStatusChange(student.enrollmentId, 'A')}
                                  variant="orange" label="A" title="Atraso"
                                />
                                <StatusBtn
                                  active={attendance[student.enrollmentId] === 'J'}
                                  onClick={() => handleStatusChange(student.enrollmentId, 'J')}
                                  variant="blue" label="J" title="Justificado"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: '34px 0', textAlign: 'center', color: 'var(--muted)', fontWeight: 800 }}>
                      Nenhum aluno matriculado nesta turma.
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Painel Lateral do Aluno */}
      <AnimatePresence>
        {selectedStudent && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedStudent(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0a1120] border-l border-white/10 z-[101] shadow-2xl p-10 flex flex-col"
            >
              <button onClick={() => setSelectedStudent(null)} className="absolute top-8 right-8 text-muted hover:text-white transition-colors">
                <XCircle size={32} />
              </button>

              <div className="flex flex-col items-center text-center mt-10 mb-12">
                <div className="w-24 h-24 rounded-[2rem] bg-purple/10 flex items-center justify-center text-purple text-4xl font-black mb-6 shadow-2xl">
                  {selectedStudent.fullName[0]}
                </div>
                <h3 className="text-2xl font-black uppercase tracking-tight text-white mb-2">{selectedStudent.fullName}</h3>
                <Badge variant="purple" className="px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{selectedStudent.enrollmentId}</Badge>
              </div>

              <div className="space-y-8 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                <div className="grid grid-cols-2 gap-4">
                  <StatCard label="Frequência" value={`${getStudentStats(selectedStudent.enrollmentId).percent}%`} sub="Geral" color="text-purple" />
                  <StatCard label="Presenças" value={getStudentStats(selectedStudent.enrollmentId).p} sub="Aulas" color="text-green" />
                  <StatCard label="Faltas" value={getStudentStats(selectedStudent.enrollmentId).f} sub="Registradas" color="text-red" />
                  <StatCard label="Justificadas" value={getStudentStats(selectedStudent.enrollmentId).j} sub="Documentadas" color="text-blue" />
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[3px] text-muted">Dados Acadêmicos</h4>
                  <div className="space-y-3">
                    <InfoRow label="Curso" value={selectedStudent.course || selectedStudent.courseName} />
                    <InfoRow label="Turma" value={`Turma ${selectedStudent.turma}`} />
                    <InfoRow label="Ingresso" value={new Date(selectedStudent.date).toLocaleDateString()} />
                  </div>
                </div>
              </div>
              
              <Button className="w-full mt-10 py-5 rounded-2xl font-black uppercase tracking-widest" onClick={() => setSelectedStudent(null)}>
                Fechar Detalhes
              </Button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function StatusBtn({ active, onClick, variant, label, title }) {
  const colors = {
    green: active ? 'bg-green text-white border-green' : 'bg-green/5 text-green/40 border-green/10 hover:bg-green/10',
    red: active ? 'bg-red text-white border-red' : 'bg-red/5 text-red/40 border-red/10 hover:bg-red/10',
    orange: active ? 'bg-orange text-white border-orange' : 'bg-orange/5 text-orange/40 border-orange/10 hover:bg-orange/10',
    blue: active ? 'bg-blue text-white border-blue' : 'bg-blue/5 text-blue/40 border-blue/10 hover:bg-blue/10',
  };

  return (
    <button 
      onClick={onClick}
      title={title}
      className={`w-12 h-12 rounded-2xl border font-black text-sm transition-all active:scale-90 flex items-center justify-center ${colors[variant]}`}
    >
      {label}
    </button>
  );
}

function SummaryItem({ label, value, color }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[10px] font-black uppercase tracking-widest opacity-60">{label}</span>
      <strong className={`text-lg font-black ${color}`}>{value}</strong>
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="bg-white/5 p-6 rounded-[2rem] border border-white/5 text-center">
      <span className="text-[8px] font-black uppercase tracking-widest text-muted block mb-1">{label}</span>
      <strong className={`text-2xl font-black block ${color}`}>{value}</strong>
      <span className="text-[8px] font-black uppercase tracking-widest opacity-20 block mt-1">{sub}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-2xl border border-white/5">
      <span className="text-[10px] font-black uppercase tracking-widest text-muted">{label}</span>
      <strong className="text-xs uppercase text-white">{value}</strong>
    </div>
  );
}
